import { apiClient } from "./client";

export type MannequinMetadata = {
  inputs: string[];
  outputs: string[];
  vehicles: string[];
  vehicle_labels: Record<string, string>;
  input_config: Record<string, {
    min: number;
    max: number;
    step: number;
    default: number;
    unit: string;
  }>;
  output_labels: Record<string, string>;
  output_config: Record<string, { min: number; max: number }>;
};

export type MannequinSurfaceRequest = {
  vehicle: string;
  seed: number;
  method: "spera" | "smooth";
  pathologies: "none" | "sampled";
  x_axis: string;
  y_axis: string;
  output: string;
  n: number;
  held: Record<string, number>;
};

export type MannequinFeature = {
  type: string;
  description?: string;
  params?: Record<string, unknown>;
  axes?: string[];
  manifold?: string;
  [key: string]: unknown;
};

export type MannequinOverlay = {
  name: string;
  /** Semantic plot layer, used by interactive legends to show/hide a layer
   * without relying on presentation copy. */
  layer?: "oracle-target" | "samples";
  mode: "lines" | "markers";
  x: number[];
  y: number[];
  z: number[];
  /** Per-marker SamplePoint ids (parallel to x/y/z) so 3-D markers can answer
   * right-click "Why this point?". Null/absent markers are not explainable. */
  ids?: Array<string | null>;
  /** Per-marker posterior standard deviation in output units. When present,
   *  the 3-D renderer draws a translucent uncertainty cloud around each mean. */
  sigma?: number[];
  /** Per-marker Monte-Carlo ensemble members (parallel to x/y/z). When a
   * marker has members, the cloud is drawn from these REAL samples instead of
   * a synthetic Gaussian, and the marker itself becomes an invisible anchor —
   * the cloud is the point. */
  mc?: Array<number[] | null>;
  line?: { color?: string; width?: number; dash?: string; opacity?: number };
  marker?: { color?: string; size?: number; symbol?: string };
};

export type MannequinComparisonSurface = {
  name: string;
  /** Semantic plot layer, used by interactive legends. */
  layer?: "prior" | "truth";
  z: number[][];
  z_range?: [number, number];
  color?: string;
  opacity?: number;
  wireOpacity?: number;
};

export type MannequinSurfaceLayerVisibility = {
  estimate: boolean;
  /** Posterior uncertainty band/colour. Sample transport clouds remain part
   * of the sample-evidence layer. */
  uncertainty: boolean;
  prior: boolean;
  truth: boolean;
  oracleTargets: boolean;
  samples: boolean;
};

export type MannequinOutputSurface = {
  z: number[][];
  z_range: [number, number];
  stats: { min: number; mean: number; max: number };
  overlays: MannequinOverlay[];
  /** Optional scalar field used only to colour the response-height mesh. */
  colour_by?: "response" | "uncertainty";
  colour_values?: number[][];
  colour_range?: [number, number];
  comparison_surfaces?: MannequinComparisonSurface[];
};

export type MannequinSurfaceResponse = {
  vehicle: string;
  seed: number;
  method: string;
  pathologies: string;
  x_axis: string;
  y_axis: string;
  output: string;
  outputs: string[];
  n: number;
  x: number[][];
  y: number[][];
  z: number[][];
  x_values: number[];
  y_values: number[];
  x_range: [number, number];
  y_range: [number, number];
  z_range: [number, number];
  features: MannequinFeature[];
  overlays: MannequinOverlay[];
  output_surfaces: Record<string, MannequinOutputSurface>;
  held: Record<string, number>;
  held_summary: string;
};

export type TailorRuntimeProvenance = {
  engine: string;
  version: string;
  surrogate: string;
  /** Total real observations supplied to this streaming Tailor operation. */
  total_observations?: number;
  /** Observations retained in the bounded posterior active set. */
  fit_observations?: number;
  approximation_mode?: string;
  active_set_cap?: number;
  effective_fit_rows?: number;
  total_transport_observations?: number;
  returned_transport_observations?: number;
  block_size?: number;
  navigator: {
    architecture: string;
    artifact: string;
    sha256: string;
    training_vehicles: number;
    training_weight_mixes: number;
    role?: string;
  };
  policy_source: string | null;
  expert_weights: Record<string, number> | null;
  output_weights?: Record<string, number> | null;
  fallback: false;
};

export type TailorRuntimeAxis = {
  name: string;
  mode: "range" | "fixed";
  min?: number;
  max?: number;
  fixed_value?: number;
};

export type TailorRuntimeRequest = {
  vehicle: string;
  seed: number;
  method?: "spera" | "smooth";
  pathologies?: "none" | "sampled";
  campaign_key: string;
  iteration: number;
  engine: "tailor" | "lhs";
  inputs: TailorRuntimeAxis[];
  outputs: string[];
  /** Relative scientific importance of each requested aerodynamic response. */
  output_weights?: Record<string, number>;
  /** Tailor's five expert weights. The unvalidated demo Navigator is not implicit. */
  expert_weights?: Record<string, number>;
  observations: Array<{ id?: string; inputs: Record<string, number> }>;
  batch_size: number;
  /** Number of points in the deterministic, sampler-independent seed design. */
  initial_design_size?: number;
  /** Generates the shared full LHS seed design; deliberately independent of sampler reseeding. */
  initial_design_seed?: number;
  /** Drives only post-seed acquisition randomness for the selected engine. */
  acquisition_seed?: number;
  symmetries?: Array<Record<string, unknown>>;
  priority?: Record<string, unknown> | null;
  post_divergence?: Record<string, unknown> | null;
  candidates_per_step?: number;
};

export type TailorRuntimeProposal = {
  inputs: Record<string, number>;
  truth_outputs: Record<string, number>;
  prior_outputs: Record<string, number>;
  predicted: Record<string, { mean: number; sigma: number }>;
  decision: {
    selected_by: string;
    reason: string;
    expert_scores: Array<{ name: string; score: number; weight: number }>;
    total_score: number | null;
    policy_source: string;
    priority_score?: number | null;
    priority_strength?: number | null;
    priority_summary?: string | null;
    /** Per-output proposal-time ARD relevance: span / (span + lengthscale).
     *  Zero is flat across the envelope; one is rapid within-envelope change. */
    ard_axis_relevance?: Record<string, Record<string, number>>;
  };
};

export type TailorProposalResponse = {
  runtime: TailorRuntimeProvenance;
  proposals: TailorRuntimeProposal[];
};

export type TailorPosteriorSurfaceRequest = TailorRuntimeRequest & {
  output: string;
  x_axis: string;
  y_axis: string;
  held: Record<string, number>;
  resolution: number;
  /** Coordinates transported onto the displayed slice. By default a 2-D
   * surface transports every non-plotted coordinate; a 1-D line also lists Y. */
  transport_axes?: string[];
  /** Axes (with values) the VIEW actually holds — including the plotted y
   * axis for a 1-D line — so the display fit's bounded row selection and
   * stall-warp detection concentrate where the user is looking. */
  selection_held?: Record<string, number>;
  /** Ensemble transport clouds: per-point Monte-Carlo samples. */
  transport_monte_carlo?: boolean;
  /** Optional lighter ensemble for small-cell views; runtime clamps [8, 64]. */
  transport_mc_members?: number;
};

export type TailorPosteriorSurfaceResponse = {
  runtime: TailorRuntimeProvenance;
  total_transport_observations?: number;
  returned_transport_observations?: number;
  transport_mc?: {
    members: number;
    row_fraction: number;
    lengthscale_jitter: number;
    method: string;
  };
  x: number[];
  y: number[];
  mean: number[][];
  sigma: number[][];
  prior_mean: number[][];
  truth_mean: number[][];
  transport: Array<{
    id: string | null;
    index: number;
    mean: number;
    sigma: number;
    correlation: number;
    oracle_mean: number;
    hidden_distance: number;
    hidden_max_delta: number;
    /** Ensemble Monte-Carlo transport samples (sampling + hyperparameter
     * sensitivity), present when the request set transport_monte_carlo. */
    mc?: number[];
    mc_sigma?: number;
  }>;
};

export type TailorAcquisitionSurfaceRequest = TailorRuntimeRequest & {
  x_axis: string;
  y_axis: string;
  held: Record<string, number>;
  resolution: number;
};

export type TailorAcquisitionSurfaceResponse = {
  runtime: TailorRuntimeProvenance;
  x: number[];
  y: number[];
  score: number[][];
};

export type TailorPosteriorEvaluationRequest = TailorRuntimeRequest & {
  output: string;
  probes: Array<Record<string, number>>;
  /** Evaluation-only regional definition. It never changes acquisition. */
  regional_evaluation?: {
    transition_half_width?: number;
    stall_grid_size: number;
    membership_smoothing: number;
    prominence_fraction: number;
    prominence_window: number;
  };
};

export type TailorRegionalErrorMetric = {
  effective_probe_count: number;
  rmse: number | null;
  gradient_rmse: number | null;
  gradient_seminorm: number | null;
  sobolev_h1: number | null;
  gradient_axis_rmse: Record<string, number>;
  /** Always the whole-probe truth range, never a region-local range. */
  truth_range: number;
  normalized_rmse: number | null;
  normalized_sobolev_h1: number | null;
};

export type TailorRegionalEvaluation = {
  axis: "alpha";
  onset_method: "local_oracle_first_sustained_cl_turnover";
  transition_half_width: number;
  stall_grid_size: number;
  membership_smoothing: number;
  prominence_fraction: number;
  prominence_window: number;
  prominence_reference: "local_oracle_cl_q95_q05";
  effective_counts: Record<"pre_stall" | "transition" | "post_stall", number>;
  outside_envelope_count: number;
  no_stall_in_envelope_count: number;
  unresolved_count: number;
  stall_onset: { min: number | null; median: number | null; max: number | null };
  normalization: "shared_global_truth_range";
  region_reduction: "membership_weighted_quadratic";
  affects_acquisition: false;
  per_output: Record<string, {
    regions: Record<"pre_stall" | "transition" | "post_stall", TailorRegionalErrorMetric>;
    outside_envelope: TailorRegionalErrorMetric;
  }>;
};

export type TailorPosteriorEvaluationResponse = {
  runtime: TailorRuntimeProvenance;
  rmse: number;
  /** RMSE of derivatives with respect to normalised input coordinates. */
  gradient_rmse?: number;
  /** Gradient L2 seminorm: sqrt(E[sum_axis derivative_error^2]). */
  gradient_seminorm?: number;
  /** Sobolev H1 error: sqrt(rmse^2 + gradient_seminorm^2). */
  sobolev_h1?: number;
  /** Per-axis derivative RMSE, with derivatives taken in normalised coordinates. */
  gradient_axis_rmse?: Record<string, number>;
  per_output?: Record<string, {
    rmse: number;
    gradient_rmse: number;
    gradient_seminorm?: number;
    sobolev_h1: number;
    gradient_axis_rmse?: Record<string, number>;
    truth_range?: number;
    normalized_rmse?: number;
    normalized_sobolev_h1?: number;
    mean_sigma?: number;
    max_sigma?: number;
  }>;
  mean_sigma: number;
  max_sigma: number;
  probe_count: number;
  regional?: TailorRegionalEvaluation;
};

export type MannequinDatabaseBriefPolicy = {
  enabled: boolean;
  strength: number;
  axes: Array<{
    axisName: string;
    target: number;
    targetWeight: number;
    farWeight: number;
    targetWidth?: number;
  }>;
  postStall?: {
    enabled: boolean;
    weight: number;
    fallbackOnset: number;
    transitionWidth?: number;
  };
};

export type MannequinDatabaseBriefTurn = {
  role: "user" | "assistant";
  content: string;
};

export type MannequinDatabaseCampaignInput = {
  name: string;
  label: string;
  unit: string;
  min: number;
  max: number;
  default: number;
};

export type MannequinDatabaseCampaign = {
  vehicle: "cessna_like" | "fighter_like" | "evtol_like";
  seed: number;
  budget: number;
  initialDesignSize: number;
  inputs: MannequinDatabaseCampaignInput[];
  symmetryMode: "off" | "mirror" | "half-and-mirror";
  symmetryAxes: string[];
};

export type MannequinDatabaseBriefRequest = {
  text: string;
  turns: MannequinDatabaseBriefTurn[];
  current?: MannequinDatabaseBriefPolicy | null;
  /** Return the validated rules interpretation immediately when false. */
  wait_for_model?: boolean;
};

export type MannequinDatabaseBriefResponse = {
  phase: "needs_input" | "ready";
  assistantMessage: string;
  questionId: string | null;
  summary: string;
  wishes: string[];
  assumptions: string[];
  policy: MannequinDatabaseBriefPolicy;
  campaign: MannequinDatabaseCampaign;
  model: string | null;
  mode: "llm" | "rules";
};

export function getMannequinMetadata(): Promise<MannequinMetadata> {
  return apiClient<MannequinMetadata>("/mannequin/metadata");
}

export function getMannequinSurface(
  request: MannequinSurfaceRequest,
): Promise<MannequinSurfaceResponse> {
  return apiClient<MannequinSurfaceResponse>("/mannequin/surface", {
    method: "POST",
    body: request,
  });
}

export function getTailorRuntime(): Promise<TailorRuntimeProvenance> {
  return apiClient<TailorRuntimeProvenance>("/mannequin/tailor/runtime");
}

export function proposeTailorBatch(
  request: TailorRuntimeRequest,
): Promise<TailorProposalResponse> {
  return apiClient<TailorProposalResponse>("/mannequin/tailor/propose", {
    method: "POST",
    body: request,
  });
}

export function getTailorPosteriorSurface(
  request: TailorPosteriorSurfaceRequest,
  signal?: AbortSignal,
): Promise<TailorPosteriorSurfaceResponse> {
  return apiClient<TailorPosteriorSurfaceResponse>("/mannequin/tailor/surface", {
    method: "POST",
    body: request,
    signal,
  });
}

export function getTailorAcquisitionSurface(
  request: TailorAcquisitionSurfaceRequest,
): Promise<TailorAcquisitionSurfaceResponse> {
  return apiClient<TailorAcquisitionSurfaceResponse>("/mannequin/tailor/acquisition", {
    method: "POST",
    body: request,
  });
}

export function evaluateTailorPosterior(
  request: TailorPosteriorEvaluationRequest,
): Promise<TailorPosteriorEvaluationResponse> {
  return apiClient<TailorPosteriorEvaluationResponse>("/mannequin/tailor/evaluate", {
    method: "POST",
    body: request,
  });
}

/** Ask the configured LLM to turn a natural-language ADB wish into a validated
 * Tailor priority draft. This endpoint never creates or mutates a campaign. */
export function interpretMannequinDatabaseBrief(
  request: MannequinDatabaseBriefRequest,
): Promise<MannequinDatabaseBriefResponse> {
  return apiClient<MannequinDatabaseBriefResponse>("/mannequin/tailor/database-brief", {
    method: "POST",
    body: request,
  });
}
